import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Scale, ArrowRight, AlertCircle, Gift, Tag, HeartPulse } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { getErrorMessage } from '../lib/utils';
import { API } from '@/config/api';

export const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const planFromUrl = searchParams.get('plan');
  const cycleFromUrl = searchParams.get('cycle');
  const refFromUrl = searchParams.get('ref');

  // Adaptación por vertical del ecosistema Punto Cero System.
  // Punto Cero Salud es la vertical por defecto; Legal fue retirado.
  const verticalParam = searchParams.get('vertical');
  const isMedicina = verticalParam !== 'legal';
  // El área/especialidad es una configuración interna del espacio Salud (no una vertical).
  const AREA_MAP = {
    medicina: 'Medicina', odontologia: 'Odontología', psicologia: 'Psicología',
    enfermeria: 'Enfermería', nutricion: 'Nutrición', terapias: 'Fisioterapia y Terapias',
    clinicas: 'Clínicas y Centros', general: 'General',
  };
  const areaLabel = AREA_MAP[(searchParams.get('area') || '').toLowerCase()] || '';
  const MED_SPECIALTIES = ['Medicina General', 'Odontología', 'Psicología', 'Enfermería', 'Nutrición', 'Fisioterapia y Terapias', 'Pediatría', 'Ginecología', 'Psiquiatría', 'Dermatología', 'Otra'];
  const LEGAL_SPECIALTIES = ['Derecho Civil', 'Derecho Penal', 'Derecho Laboral', 'Derecho Comercial', 'Derecho de Familia', 'Derecho Migratorio', 'Derecho Tributario', 'Derecho Corporativo', 'Propiedad Intelectual'];
  const PRACTICE_TYPES = ['Consultorio independiente', 'Clínica', 'Hospital', 'Centro de salud', 'Centro médico', 'Teleconsulta'];
  const theme = isMedicina
    ? { name: 'Punto Cero Salud', grad: 'from-[#14b8a6] to-[#06b6d4]', accent: '#14b8a6', title: 'Crea tu espacio profesional de salud', subtitle: 'Organiza tu práctica profesional desde el primer día' }
    : { name: 'Punto Cero Legal', grad: 'from-[#f97316] to-[#fb923c]', accent: '#f97316', title: 'Únete a la red más grande de LATAM', subtitle: 'Comienza tu prueba gratuita de 7 días' };

  const [referralValid, setReferralValid] = useState(null);
  const [referrerName, setReferrerName] = useState('');
  const [formData, setFormData] = useState({
    email: '', password: '', full_name: '', phone: '',
    country: 'Colombia',
    specialty: isMedicina ? (areaLabel || 'General') : 'Derecho Civil',
    bar_number: '', id_document: '', firm_name: '',
    role: 'lawyer',
    vertical: isMedicina ? 'salud' : 'legal',
    practice_type: isMedicina ? 'Consultorio independiente' : '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedLegal, setAcceptedLegal] = useState(false);

  useEffect(() => {
    if (refFromUrl) {
      axios.get(`${API}/referrals/validate/${refFromUrl}`).then(res => {
        setReferralValid(res.data.valid);
        if (res.data.valid) setReferrerName(res.data.referrer_name);
      }).catch(() => setReferralValid(false));
    }
  }, [refFromUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!acceptedLegal) {
      setError('Debes aceptar el Contrato de Suscripción Profesional y las políticas para registrarte.');
      return;
    }
    setLoading(true);
    try {
      // Campos de aceptación legal (aditivos; no alteran la autenticación).
      const newUser = await register({ ...formData, accepted_legal: true, accepted_at: new Date().toISOString() });
      // Si requiere verificación → /verificacion-pendiente
      if (newUser?.is_verified === false || newUser?.status === 'PENDING_VERIFICATION') {
        navigate('/verificacion-pendiente');
      } else if (planFromUrl) {
        navigate(`/checkout?plan=${planFromUrl}&cycle=${cycleFromUrl || 'monthly'}${refFromUrl ? `&ref=${refFromUrl}` : ''}`);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Error al registrarse'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] flex items-center justify-center px-6 py-12 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#3b82f6]/30 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#10b981]/30 rounded-full blur-3xl" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl relative z-10">
        <Link to={isMedicina ? '/system/medicina' : '/'} className="flex items-center justify-center gap-2 mb-8">
          {isMedicina ? <HeartPulse className="w-10 h-10 text-[#14b8a6]" /> : <Scale className="w-10 h-10 text-[#f97316]" />}
          <span className="text-3xl font-bold text-white">{theme.name}</span>
        </Link>

        <div className="backdrop-blur-xl bg-white/5 rounded-3xl p-8 border border-white/20 shadow-2xl">
          <h1 className="text-3xl font-bold text-white mb-2">{theme.title}</h1>
          <p className="text-white/60 mb-6">{theme.subtitle}</p>

          {planFromUrl && (
            <div className="mb-4 p-3 rounded-xl bg-[#f97316]/10 border border-[#f97316]/30 flex items-center gap-2 text-[#f97316]">
              <Tag className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">Plan seleccionado: <strong className="capitalize">{planFromUrl}</strong> · {cycleFromUrl === 'annual' ? 'Anual (1 mes gratis)' : 'Mensual'}</span>
            </div>
          )}

          {refFromUrl && referralValid && (
            <div className="mb-4 p-3 rounded-xl bg-[#10b981]/10 border border-[#10b981]/30 flex items-center gap-2 text-[#10b981]">
              <Gift className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">Llegaste por referido de <strong>{referrerName}</strong>. Él recibirá 1 mes gratis cuando completes tu pago.</span>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">Nombre Completo</label>
                <Input type="text" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className="bg-white/10 border-white/20 text-white" placeholder="Dr. Juan Pérez" required data-testid="register-name" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">Correo</label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-white/10 border-white/20 text-white" placeholder={isMedicina ? 'profesional@ejemplo.com' : 'abogado@ejemplo.com'} required data-testid="register-email" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">Teléfono</label>
                <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="bg-white/10 border-white/20 text-white" placeholder="+57 300 123 4567" required data-testid="register-phone" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">País</label>
                <select value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white" data-testid="register-country">
                  <option>Colombia</option><option>Venezuela</option><option>México</option>
                  <option>Argentina</option><option>Chile</option><option>Perú</option>
                  <option>Ecuador</option><option>España</option><option>Estados Unidos</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">{isMedicina ? 'Área profesional' : 'Especialidad'}</label>
                {isMedicina ? (
                  <div className="w-full px-4 py-3 rounded-xl bg-white/5 border border-dashed border-white/20 text-white/70 text-sm" data-testid="register-area-note">
                    {areaLabel
                      ? <>Área seleccionada: <strong className="text-[#14b8a6]">{areaLabel}</strong>. Podrás ajustarla desde tu perfil profesional.</>
                      : 'Podrás indicar tu especialidad o área desde tu perfil profesional.'}
                  </div>
                ) : (
                  <select value={formData.specialty} onChange={(e) => setFormData({ ...formData, specialty: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white" data-testid="register-specialty">
                    {LEGAL_SPECIALTIES.map((s) => (<option key={s}>{s}</option>))}
                  </select>
                )}
              </div>
              {isMedicina && (
                <div>
                  <label className="block text-sm font-semibold text-white/80 mb-2">Tipo de práctica</label>
                  <select value={formData.practice_type} onChange={(e) => setFormData({ ...formData, practice_type: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white" data-testid="register-practice-type">
                    {PRACTICE_TYPES.map((p) => (<option key={p}>{p}</option>))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">{isMedicina ? 'Registro / Matrícula profesional *' : 'Tarjeta Profesional *'}</label>
                <Input type="text" value={formData.bar_number} onChange={(e) => setFormData({ ...formData, bar_number: e.target.value })} className="bg-white/10 border-white/20 text-white" placeholder={isMedicina ? 'RP-123456' : 'TP-123456'} required data-testid="register-bar" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">Cédula / Documento *</label>
                <Input type="text" value={formData.id_document} onChange={(e) => setFormData({ ...formData, id_document: e.target.value })} className="bg-white/10 border-white/20 text-white" placeholder="CC 1.234.567.890" required data-testid="register-id-document" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-white/80 mb-2">{isMedicina ? 'Consultorio / Centro profesional *' : 'Nombre del Bufete / Firma *'}</label>
              <Input type="text" value={formData.firm_name} onChange={(e) => setFormData({ ...formData, firm_name: e.target.value })} className="bg-white/10 border-white/20 text-white" placeholder={isMedicina ? 'Consultorio / Clínica Santa María' : 'Despacho Pérez & Asociados'} required data-testid="register-firm-name" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-white/80 mb-2">Contraseña</label>
              <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="bg-white/10 border-white/20 text-white" placeholder="Mínimo 8 caracteres" minLength={8} required data-testid="register-password" />
            </div>

            <label className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
              <input type="checkbox" checked={acceptedLegal} onChange={(e) => setAcceptedLegal(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#f97316] flex-shrink-0" required data-testid="register-accept-legal" />
              <span className="text-xs text-white/70 leading-relaxed">
                He leído y acepto el <Link to="/subscription-agreement" target="_blank" className="text-[#f97316] hover:underline">Contrato de Suscripción Profesional</Link>,
                los <Link to="/terms" target="_blank" className="text-[#f97316] hover:underline">Términos y Condiciones</Link>,
                la <Link to="/privacy" target="_blank" className="text-[#f97316] hover:underline">Política de Privacidad</Link> y
                la <Link to="/cookies" target="_blank" className="text-[#f97316] hover:underline">Política de Cookies</Link>.
              </span>
            </label>

            <Button type="submit" disabled={loading || !acceptedLegal} className={`w-full bg-gradient-to-r ${theme.grad} hover:shadow-[0_10px_30px_rgba(0,0,0,0.35)] text-white font-bold py-6 disabled:opacity-50 disabled:cursor-not-allowed`} data-testid="register-submit">
              {loading ? 'Creando cuenta...' : 'Crear Cuenta · Solicitar Verificación'}
              {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
            </Button>
            <p className="text-xs text-white/40 text-center mt-2">
              🔒 Tu cuenta será revisada por nuestro equipo de compliance antes de la activación.
            </p>
          </form>

          <p className="text-center text-white/60 mt-6">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-[#f97316] hover:underline font-semibold">Inicia sesión</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
